import { _ as _object_spread } from "@swc/helpers/_/_object_spread";
import { _ as _object_spread_props } from "@swc/helpers/_/_object_spread_props";
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Controller, useForm } from 'react-hook-form';
import { Column, ConfirmationModal, FullColumn, IconButton, Row, TextField, Typography, useOverlayContext } from 'seagull';
import { ColorThumbDown, ColorThumbUp, ThumbDown, ThumbUp } from 'seagull/icons';
import { useSubmitServiceDescriptionFeedback } from '~/hooks/reactQuery/mutations/useSubmitServiceDescriptionFeedback';
import { useLocaleKeys } from '~/i18n';
import { useStoreActions } from '~/store/store-context';
import { FeedbackModalTestIds } from './FeedbackModal.utils';
const FeedbackModal = (param)=>{
    let { conversationId, onClose } = param;
    const { logBI } = useStoreActions();
    const { submitFeedback, isLoading } = useSubmitServiceDescriptionFeedback();
    const { showSuccessToast } = useOverlayContext();
    const { t: feedbackTexts } = useLocaleKeys('helpMeWrite.feedback');
    const { control, handleSubmit, formState: { errors } } = useForm({
        mode: 'onSubmit',
        defaultValues: {
            rating: null,
            comment: ''
        }
    });
    const showError = !!errors.rating;
    const handleRatingChange = (newRating)=>{
        if (newRating === 'thumbsUp') {
            logBI(17012);
        } else if (newRating === 'thumbsDown') {
            logBI(17013);
        }
    };
    const onSubmit = async (data)=>{
        const feedbackText = data.comment.trim() || undefined;
        logBI(17014, {
            feedbackText
        });
        await submitFeedback({
            conversationId,
            data: {
                rating: data.rating,
                comment: feedbackText
            }
        });
        showSuccessToast(feedbackTexts.successToast(), FeedbackModalTestIds.SuccessToast());
        onClose();
    };
    return /*#__PURE__*/ _jsx(ConfirmationModal, {
        bi: {
            primaryButton: {
                name: 'feedback_confirm'
            },
            secondaryButton: {
                name: 'feedback_cancel'
            }
        },
        width: "550px",
        title: feedbackTexts.title(),
        showFooter: true,
        onPrimaryButtonClick: handleSubmit(onSubmit),
        primaryButtonLabel: feedbackTexts.submitButton(),
        primaryButtonLoading: isLoading,
        primaryButtonTestId: FeedbackModalTestIds.SubmitButton(),
        secondaryButtonLabel: feedbackTexts.closeButton(),
        onSecondaryButtonClick: onClose,
        secondaryButtonTestId: FeedbackModalTestIds.CloseButton(),
        content: /*#__PURE__*/ _jsxs(FullColumn, {
            gap: 16,
            children: [
                /*#__PURE__*/ _jsxs(Row, {
                    alignItems: "center",
                    gap: 8,
                    children: [
                        /*#__PURE__*/ _jsx(Typography, {
                            variant: "paragraph1",
                            testId: FeedbackModalTestIds.RatingQuestion(),
                            children: feedbackTexts.ratingQuestion()
                        }),
                        /*#__PURE__*/ _jsx(Controller, {
                            control: control,
                            name: "rating",
                            rules: {
                                required: feedbackTexts.ratingRequired()
                            },
                            render: (param)=>{
                                let { field: { onChange, value } } = param;
                                return /*#__PURE__*/ _jsxs(Row, {
                                    gap: 4,
                                    children: [
                                        /*#__PURE__*/ _jsx(IconButton, {
                                            bi: {
                                                name: 'help_me_write_feedback_thumbs_up'
                                            },
                                            onClick: ()=>{
                                                const newValue = value === 'thumbsUp' ? null : 'thumbsUp';
                                                handleRatingChange(newValue);
                                                onChange(newValue);
                                            },
                                            testId: FeedbackModalTestIds.ThumbsUpButton(),
                                            "data-selected": value === 'thumbsUp',
                                            "data-error": showError && !value ? 'true' : undefined,
                                            skin: showError && !value ? 'error' : 'standard',
                                            children: value === 'thumbsUp' ? /*#__PURE__*/ _jsx(ColorThumbUp, {}) : /*#__PURE__*/ _jsx(ThumbUp, {})
                                        }),
                                        /*#__PURE__*/ _jsx(IconButton, {
                                            bi: {
                                                name: 'help_me_write_feedback_thumbs_down'
                                            },
                                            onClick: ()=>{
                                                const newValue = value === 'thumbsDown' ? null : 'thumbsDown';
                                                handleRatingChange(newValue);
                                                onChange(newValue);
                                            },
                                            testId: FeedbackModalTestIds.ThumbsDownButton(),
                                            "data-selected": value === 'thumbsDown',
                                            "data-error": showError && !value ? 'true' : undefined,
                                            skin: showError && !value ? 'error' : 'standard',
                                            children: value === 'thumbsDown' ? /*#__PURE__*/ _jsx(ColorThumbDown, {}) : /*#__PURE__*/ _jsx(ThumbDown, {})
                                        })
                                    ]
                                });
                            }
                        })
                    ]
                }),
                /*#__PURE__*/ _jsxs(Column, {
                    gap: 8,
                    alignItems: "start",
                    children: [
                        /*#__PURE__*/ _jsx(Typography, {
                            variant: "paragraph1",
                            testId: FeedbackModalTestIds.CommentLabel(),
                            children: feedbackTexts.commentLabel()
                        }),
                        /*#__PURE__*/ _jsx(Controller, {
                            control: control,
                            name: "comment",
                            render: (param)=>{
                                let { field } = param;
                                return /*#__PURE__*/ _jsx(TextField, _object_spread_props(_object_spread({
                                    bi: {
                                        name: 'feedback_comment'
                                    },
                                    collapseHelperText: false
                                }, field), {
                                    multiline: true,
                                    minRows: 3,
                                    maxRows: 3,
                                    placeholder: feedbackTexts.commentPlaceholder(),
                                    testId: FeedbackModalTestIds.CommentInput()
                                }));
                            }
                        })
                    ]
                })
            ]
        }),
        onCloseButtonClick: onClose,
        testId: FeedbackModalTestIds.Root()
    });
};
export default FeedbackModal;
